import { css } from "emotion";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IIconProps,
  IStackProps,
  PrimaryButton,
  ScrollablePane,
  Stack
} from "office-ui-fabric-react";
import React from "react";
import {
  Field,
  InjectedFormProps,
  reduxForm,
  SubmissionError
} from "redux-form";
import {
  AssignGroupingKey,
  DefaultGroupingExpansionState,
  DialogProps
} from "../../../types/types";
import { isEmptyObject } from "../../../util/util";
import { renderChoiceGroup, renderTextField } from "../../FormFields";

export const customFilterRegistrationFormId = "CustomFilterRegistrationForm";

export interface CustomFilterRegistrationDialogProps {
  dialogProps: DialogProps;
  submit: (values: any) => void;
  close: () => void;
}

/**
 * カスタムフィルター登録ダイアログ
 * @param props
 * @constructor
 */
export const CustomFilterRegistrationDialog = (
  props: CustomFilterRegistrationDialogProps
) => {
  const { dialogProps, submit, close } = props;

  const groupingOptions: Array<{
    key: AssignGroupingKey;
    iconProps: IIconProps;
    text: string;
  }> = [
    {
      key: "jobCode",
      iconProps: { iconName: "ActivateOrders" },
      text: "ジョブ"
    },
    {
      key: "inChargeKojinCode",
      iconProps: { iconName: "Contact" },
      text: "担当"
    }
  ];

  const defaultGroupingExpansionStateOptions: Array<{
    key: DefaultGroupingExpansionState;
    iconProps: IIconProps;
    text: string;
  }> = [
    {
      key: "expand",
      iconProps: { iconName: "Move" },
      text: "展開"
    },
    {
      key: "collapse",
      iconProps: { iconName: "BackToWindow" },
      text: "折り畳む"
    }
  ];

  const Form = (
    formProps: InjectedFormProps<{
      groupingCondition: string;
      defaultGroupingExpansionState: string;
    }>
  ) => {
    const { handleSubmit, submitting } = formProps;

    const columnProps: Partial<IStackProps> = {
      tokens: { childrenGap: 10 },
      styles: { root: { width: 300 } }
    };

    return (
      <Dialog
        hidden={!dialogProps.isOpen}
        onDismiss={close}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: dialogProps.title
        }}
        modalProps={{
          isBlocking: dialogProps.isModal,
          // 元々Dialogコンポーネントに設定されているmax-widthも拡張しないとダメ
          containerClassName: css`
            height: 500px;
            width: 500px;
            max-width: 400px;
          `
        }}
      >
        <form
          id="customFilterRegistrationForm"
          // TODO: 共通化
          // https://github.com/erikras/redux-form/issues/3949
          onSubmit={handleSubmit(values => {
            return new Promise((resolve, reject) => {
              const errors = validate(values);
              console.log({ errors });
              if (errors) {
                reject(new SubmissionError(errors));
              } else {
                submit(values);
                resolve();
              }
            });
          })}
          noValidate={true}
        >
          <div
            className={css`
              height: 350px;
            `}
          >
            <ScrollablePane>
              <Stack horizontal={true} tokens={{ childrenGap: 50 }}>
                <Stack {...columnProps}>
                  <div>
                    <Field
                      name="filterName"
                      label="フィルター名"
                      type="text"
                      maxLength={20}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="groupingCondition"
                      label="デフォルトグルーピング条件"
                      type="text"
                      maxLength={20}
                      component={renderChoiceGroup(groupingOptions)}
                    />
                  </div>
                  <div>
                    <Field
                      name="defaultGroupingExpansionState"
                      label="デフォルトデータ展開状態"
                      type="text"
                      maxLength={20}
                      defaultSelectedKey={"expand"}
                      component={renderChoiceGroup(
                        defaultGroupingExpansionStateOptions
                      )}
                    />
                  </div>
                </Stack>
              </Stack>
            </ScrollablePane>
          </div>
        </form>
        <DialogFooter>
          <PrimaryButton
            text="登録"
            type="submit"
            form="customFilterRegistrationForm"
            disabled={submitting}
          />
          <DefaultButton onClick={close} text="閉じる" disabled={submitting} />
        </DialogFooter>
      </Dialog>
    );
  };

  const ConnectedForm = reduxForm({
    form: customFilterRegistrationFormId,
    initialValues: {
      groupingCondition: "jobCode",
      defaultGroupingExpansionState: "expand"
    }
    // destroyOnUnmount: false,
    // enableReinitialize: true,
    // keepDirtyOnReinitialize: true
  })(Form);
  return <ConnectedForm onSubmit={submit} />;
};

const validate = (values: any) => {
  const errors: any = {};

  if (!values.filterName) {
    errors.filterName = "フィルター名は必須です";
  }

  return isEmptyObject(errors) ? undefined : errors;
};
